import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, Save, Loader2, Globe, Lock, Unlock, Eye, Link, Code, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminPageEditor = () => {
  const navigate = useNavigate();
  const { pageId } = useParams();
  const { t } = useTranslation();
  const { token, isAdmin } = useAuth();
  const isEditMode = !!pageId;
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    visibility: 'general',
    seo: {
      meta_title: '',
      meta_description: '',
      og_image: ''
    },
    is_active: true
  });

  // Get current domain for preview
  const currentDomain = typeof window !== 'undefined' ? window.location.origin : '';

  useEffect(() => {
    if (!isAdmin) {
      navigate('/home');
      return;
    }
    if (isEditMode) {
      fetchPage();
    }
  }, [pageId, isAdmin]);

  const fetchPage = async () => {
    try {
      const res = await axios.get(`${API}/admin/pages/${pageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData({
        title: res.data.title,
        slug: res.data.slug,
        content: res.data.content,
        visibility: res.data.visibility,
        seo: res.data.seo || { meta_title: '', meta_description: '', og_image: '' },
        is_active: res.data.is_active
      });
    } catch (err) {
      toast.error(t('common.error'));
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (value) => {
    setFormData(prev => ({
      ...prev,
      title: value,
      slug: prev.slug || generateSlug(value)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.slug || !formData.content) {
      toast.error(t('messages.fillRequired'));
      return;
    }

    setSaving(true);
    try {
      if (isEditMode) {
        await axios.put(`${API}/admin/pages/${pageId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('messages.updatedSuccessfully'));
      } else {
        await axios.post(`${API}/admin/pages`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('messages.createdSuccessfully'));
      }
      navigate('/admin?tab=pages');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/pages/${pageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('messages.deletedSuccessfully'));
      navigate('/admin?tab=pages');
    } catch (err) {
      toast.error(t('common.error'));
    }
    setShowDeleteConfirm(false);
  };

  // Custom upload adapter for CKEditor - Convert to Base64
  class UploadAdapter {
    constructor(loader) {
      this.loader = loader;
    }

    upload() {
      return this.loader.file.then(file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = () => {
          resolve({
            default: reader.result // This is the base64 data URL
          });
        };
        
        reader.onerror = (error) => {
          reject(error);
        };
        
        reader.readAsDataURL(file);
      }));
    }

    abort() {}
  }

  function uploadAdapterPlugin(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
      return new UploadAdapter(loader);
    };
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#009DAE] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#009DAE]">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-[#0e2a45]/95 backdrop-blur-lg border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin?tab=pages')}
              className="p-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-zinc-400" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-white">
                {isEditMode ? t('adminPage.editPage') : t('adminPage.createPage')}
              </h1>
              <p className="text-zinc-500 text-sm">
                {isEditMode ? t('adminPage.updatePage') : t('adminPage.createDesc')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isEditMode && (
              <Button
                variant="outline"
                onClick={() => window.open(`${currentDomain}/${formData.slug}`, '_blank')}
                className="border-zinc-700 text-zinc-400"
              >
                <Eye className="w-4 h-4 mr-2" /> {t('adminPage.preview')}
              </Button>
            )}
            {isEditMode && (
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(true)}
                className="border-cyan-900 text-cyan-500 hover:bg-cyan-900/20"
              >
                <Trash2 className="w-4 h-4 mr-2" /> {t('common.delete')}
              </Button>
            )}
            <Button 
              onClick={handleSubmit} 
              disabled={saving}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {isEditMode ? t('adminPage.updateButton') : t('adminPage.publishPage')}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - CKEditor */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <label className="text-zinc-400 text-sm block mb-2">{t('adminPage.pageTitle')} *</label>
              <Input
                value={formData.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder={t('adminPage.enterTitle')}
                className="bg-zinc-800 border-zinc-700 text-white text-lg h-12"
              />
            </div>

            {/* Content Editor */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <label className="text-zinc-400 text-sm block mb-3">{t('adminPage.content')} *</label>
              <div className="bg-white rounded-xl overflow-hidden">
                <CKEditor
                  editor={ClassicEditor}
                  data={formData.content}
                  config={{
                    extraPlugins: [uploadAdapterPlugin],
                    toolbar: {
                      items: [
                        'heading', '|',
                        'fontSize', 'fontColor', 'fontBackgroundColor', '|',
                        'bold', 'italic', 'underline', 'strikethrough', '|',
                        'link', 'bulletedList', 'numberedList', '|',
                        'alignment', 'indent', 'outdent', '|',
                        'imageUpload', 'blockQuote', 'insertTable', 'mediaEmbed', '|',
                        'horizontalLine', 'specialCharacters', '|',
                        'undo', 'redo', '|',
                        'sourceEditing'
                      ],
                      shouldNotGroupWhenFull: true
                    },
                    heading: {
                      options: [
                        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                        { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                        { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                        { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                        { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' }
                      ]
                    },
                    fontSize: {
                      options: ['tiny', 'small', 'default', 'big', 'huge']
                    },
                    image: {
                      toolbar: ['imageTextAlternative', 'imageStyle:full', 'imageStyle:side']
                    },
                    table: {
                      contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
                    },
                    placeholder: 'Start writing your content here...'
                  }}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setFormData(prev => ({ ...prev, content: data }));
                  }}
                />
              </div>
              <style>{`
                .ck-editor__editable {
                  min-height: 500px !important;
                  max-height: 700px !important;
                }
                .ck-toolbar {
                  background: #f4f4f5 !important;
                  border-bottom: 1px solid #e4e4e7 !important;
                }
                .ck-content {
                  font-size: 16px;
                  line-height: 1.7;
                }
                .ck-content h1 { font-size: 2em; font-weight: bold; margin: 0.67em 0; }
                .ck-content h2 { font-size: 1.5em; font-weight: bold; margin: 0.83em 0; }
                .ck-content h3 { font-size: 1.17em; font-weight: bold; margin: 1em 0; }
                .ck-content p { margin: 1em 0; }
                .ck-content blockquote { 
                  border-left: 4px solid #18BFE0;
                  padding-left: 1em;
                  margin: 1em 0;
                  color: #666;
                }
              `}</style>
              {formData.visibility === 'login_required' && (
                <div className="mt-4 p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                  <p className="text-zinc-400 text-sm flex items-center gap-2">
                    <Code className="w-4 h-4" />
                    {t('adminPage.dynamicParams')}: 
                    <span className="font-mono text-yellow-500">{'{name}'}</span>
                    <span className="font-mono text-yellow-500">{'{email}'}</span>
                    <span className="font-mono text-yellow-500">{'{country}'}</span>
                    <span className="font-mono text-yellow-500">{'{coins}'}</span>
                    <span className="font-mono text-yellow-500">{'{tickets}'}</span>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Settings */}
          <div className="space-y-6">
            {/* URL & Slug */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                <Link className="w-4 h-4 text-blue-500" /> {t('adminPage.urlSettings')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-zinc-400 text-sm block mb-2">{t('adminPage.slug')} *</label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 bg-zinc-700 border border-r-0 border-zinc-600 rounded-l-md text-zinc-400 text-sm">
                      /
                    </span>
                    <Input
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: generateSlug(e.target.value) })}
                      placeholder="page-slug"
                      className="bg-zinc-800 border-zinc-700 text-white rounded-l-none"
                    />
                  </div>
                </div>
                <div className="p-3 bg-zinc-800/50 rounded-lg">
                  <p className="text-zinc-500 text-xs">{t('adminPage.previewUrl')}</p>
                  <p className="text-zinc-300 text-sm truncate mt-1">
                    {currentDomain}/{formData.slug || 'your-slug'}
                  </p>
                </div>
              </div>
            </div>

            {/* Visibility & Status */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h3 className="text-white font-medium mb-4">{t('adminPage.pageSettings')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-zinc-400 text-sm block mb-2">{t('adminPage.visibility')}</label>
                  <Select
                    value={formData.visibility}
                    onValueChange={(v) => setFormData({ ...formData, visibility: v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="general">
                        <span className="flex items-center gap-2">
                          <Unlock className="w-4 h-4 text-green-500" /> {t('adminPage.public')}
                        </span>
                      </SelectItem>
                      <SelectItem value="login_required">
                        <span className="flex items-center gap-2">
                          <Lock className="w-4 h-4 text-yellow-500" /> {t('adminPage.loginRequired')}
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-zinc-400 text-sm block mb-2">{t('adminPage.status')}</label>
                  <Select
                    value={formData.is_active ? 'active' : 'draft'}
                    onValueChange={(v) => setFormData({ ...formData, is_active: v === 'active' })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="active">{t('adminPage.published')}</SelectItem>
                      <SelectItem value="draft">{t('adminPage.draft')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* SEO Settings */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                <Globe className="w-4 h-4 text-green-500" /> {t('adminPage.seoSettings')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-zinc-400 text-sm block mb-2">{t('adminPage.metaTitle')}</label>
                  <Input
                    value={formData.seo.meta_title}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      seo: { ...formData.seo, meta_title: e.target.value } 
                    })}
                    placeholder={t('adminPage.metaTitle')}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-zinc-400 text-sm block mb-2">{t('adminPage.metaDescription')}</label>
                  <Textarea
                    value={formData.seo.meta_description}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      seo: { ...formData.seo, meta_description: e.target.value } 
                    })}
                    placeholder={t('adminPage.seoDescription')}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="text-zinc-400 text-sm block mb-2">{t('adminPage.ogImage')}</label>
                  <Input
                    value={formData.seo.og_image}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      seo: { ...formData.seo, og_image: e.target.value } 
                    })}
                    placeholder="https://example.com/image.jpg"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{t('adminPage.deletePage')}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {t('adminPage.deleteConfirm')} "{formData.title}"? {t('admin.cannotUndo')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPageEditor;
